import * as apiTo from '@api/api-routes'
import cleaveMixin from '@/mixins/cleave-mixin'
import { validations } from './validations'
import { getFinancialPeriod } from '@/utils/get-financial-period'
// Libraries
import { mapGetters, mapMutations, mapActions } from 'vuex'
import { cloneDeep } from 'lodash'
import moment from 'moment'
// Components
import LmDialog from '@/reusable/base-components/LmDialog/LmDialog'
import EnhancedQSelect from '@components/general/EnhancedQSelect'
import LmDatePicker from '@/reusable/LmDatePicker/LmDatePicker'
import FormCreateProvider from '@components/forms/form-create-provider/FormCreateProvider'
import LinkButton from '@/reusable/base-components/LinkButton'
import MilestoneTasks from '@/reusable/milestone-tasks/MilestoneTasks'

export default {
  components: {
    LmDialog,
    EnhancedQSelect,
    LmDatePicker,
    FormCreateProvider,
    LinkButton,
    MilestoneTasks
  },

  mixins: [cleaveMixin, validations],

  props: {
    value: Boolean,
    editItem: Object,
    product: Object
  },

  data: () => ({
    data: {
      one_time_purchase: false,
      name_reference: null,
      financial_period_id: null,
      product_id: null,
      start_date: null,
      end_date: null,
      vendor_id: null,
      owner_id: null,
      cancellation_deadline: null,
      currency: null,
      price: null,
      normalized_price: null,
      forecasted_price: null,
      task_applicability: null
    },
    providerModal: false
  }),

  computed: {
    ...mapGetters('subscriptions/subscriptionPeriods', [
      'vendors',
      'products',
      'vendorOptions',
      'ownerOptions',
      'productOptions',
      'financialPeriods'
    ]),

    ...mapGetters('subscription-show', ['subscription']),

    showModal: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      }
    },

    isOriginalCurrencySameAsLocalCurrency() {
      return this.data.currency === this.settings.currency
    },

    financialPeriodOptions() {
      return this.financialPeriods.map(period => ({
        label: `${this.formatDateForLocale(period.start_date)} - ${this.formatDateForLocale(period.end_date)}`,
        value: period.id
      }))
    }
  },

  watch: {
    editItem: {
      handler: 'initData',
      immediate: true
    },
    'data.vendor_id': 'updateCurrencyFromVendor',
    'data.start_date'(val) {
      if (
        moment(val, this.settings.date_format).isBefore(
          moment(this.data.cancellation_deadline, this.settings.date_format).format('YYYY-MM-DD')
        )
      ) {
        this.data.cancellation_deadline = null
      }
      if (!this.data.one_time_purchase) {
        this.data.end_date = moment(val, this.settings.date_format)
          .add(1, 'year')
          .subtract(1, 'day')
          .format(this.settings.date_format)
      }
    },
    isOriginalCurrencySameAsLocalCurrency(currenciesAreTheSame) {
      if (currenciesAreTheSame) {
        this.data.price = this.data.normalized_price
      }
    }
  },

  mounted() {
    this.inputPriceFormat('#normalized_price .q-field__native', 2, ({ target: valueChanged }) => {
      this.data.normalized_price = valueChanged.value
      if (this.isOriginalCurrencySameAsLocalCurrency) {
        this.data.price = valueChanged.value
      }
    })
    this.inputPriceFormat('#price .q-field__native', 2, ({ target: valueChanged }) => {
      this.data.price = valueChanged.value
      if (this.isOriginalCurrencySameAsLocalCurrency) {
        this.data.normalized_price = valueChanged.value
      }
    })
    this.inputPriceFormat('#forecasted_price .q-field__native', 2, ({ target: valueChanged }) => {
      this.data.forecasted_price = valueChanged.value
    })
  },

  methods: {
    ...mapMutations('subscription-show', ['updatePeriod']),
    ...mapActions('subscription-show', ['fetchSubscription']),

    isDateBeforeStartDate(date) {
      return moment(date, 'YYYY/MM/DD').isBefore(
        moment(this.data.start_date, this.settings.date_format).format('YYYY-MM-DD')
      )
    },

    isDateAfterStartDate(date) {
      if (!this.data.start_date) return true
      return moment(date, 'YYYY/MM/DD').isAfter(
        moment(this.data.start_date, this.settings.date_format).format('YYYY-MM-DD')
      )
    },

    initData(data) {
      let endDate = ''
      if (!data.one_time_purchase && data.end_date)
        endDate = moment(data.end_date, 'YYYY-MM-DD').format(this.settings.date_format)

      const financialPeriodToPreselect = getFinancialPeriod(
        data.start_date,
        this.financialPeriods,
        this.settings.date_format
      )

      this.data.one_time_purchase = data.one_time_purchase
      this.data.product_id = this.product.id
      this.data.name_reference = data.name_reference
      this.data.financial_period_id = financialPeriodToPreselect?.id
      this.data.start_date = moment(data.start_date, 'YYYY-MM-DD').format(this.settings.date_format)
      this.data.end_date = endDate
      this.data.vendor_id = data.vendor ? data.vendor.id : null
      this.data.owner_id = data.owner ? data.owner.id : null
      this.data.cancellation_deadline = data.cancellation_deadline
        ? moment(data.cancellation_deadline, 'YYYY-MM-DD').format(this.settings.date_format)
        : null
      this.data.currency = data.currency
      this.data.price = this.formatPrice(data.price)
      this.data.normalized_price = this.formatPrice(data.normalized_price)
      this.data.forecasted_price = this.formatPrice(data.forecasted_price)
      this.data.task_applicability = cloneDeep(data.task_applicability)
    },
    formatPriceForDisplay(field) {
      const formattedPriceNumber = this.formatInputNumber(this.data[field]) || null
      this.data[field] = formattedPriceNumber
      if (this.isOriginalCurrencySameAsLocalCurrency) {
        if (field === 'normalized_price') {
          this.data.price = formattedPriceNumber
        } else if (field === 'price') {
          this.data.normalized_price = formattedPriceNumber
        }
      }
      return this.data[field]
    },
    setVendorId(vendor) {
      this.$store.dispatch('subscriptions/subscriptionPeriods/setVendorId', vendor.id)
      this.$store
        .dispatch('subscriptions/subscriptionPeriods/getCreateFormData')
        .then(() => (this.data.vendor_id = vendor.id))
    },
    updateCurrencyFromVendor(vendorId) {
      if (!vendorId && this.data.product_id) {
        this.updateCurrencyFromProduct(this.data.product_id)
        return
      }
      const vendor = this.vendors.find(item => item.id === vendorId)
      if (vendor) this.data.currency = vendor.currency
    },
    updateCurrencyFromProduct(productId) {
      if (!this.data.vendor_id) {
        const product = this.products.find(item => item.id === productId)
        if (product) this.data.currency = product.publisher.currency
      }
    },
    onSave() {
      this.$refs.priceInput.$emit('blur')
      this.$refs.normalizedPriceInput.$emit('blur')

      this.$v.$touch()
      if (this.$v.$invalid) return

      apiTo.updateSubscriptionPeriod(this.editItem.id, this.getDataForApi(this.data)).then(({ data }) => {
        this.updatePeriod(data.result)
        this.fetchSubscription(this.subscription.id)
        this.$root.$emit('refresh-all-tasks', this.editItem.id)
        this.$root.$emit('refresh-milestone-tabs', this.editItem.id)
        this.showModal = false
      })
    },

    getDataForApi(data) {
      let payload = cloneDeep(data)
      payload.price = this.convertToNumber(payload.price) || null
      payload.normalized_price = this.convertToNumber(payload.normalized_price) || null
      payload.forecasted_price = this.convertToNumber(payload.forecasted_price) || null
      payload.start_date = this.formatDateForPost(payload.start_date, this.settings.date_format)
      payload.end_date = this.formatDateForPost(payload.end_date, this.settings.date_format)
      payload.cancellation_deadline = this.formatDateForPost(payload.cancellation_deadline, this.settings.date_format)
      return payload
    }
  }
}
